<template>
  <b-modal
    ref="modal"
    :visible="show"
    size="lg"
    hide-footer
    body-class="bg-white"
    :title="mode === 'edit' ? 'Edit Do not send entry' : 'Add to Do not send list'"
    :no-close-on-backdrop="saving"
    :no-close-on-esc="saving"
    @hidden="onHidden"
  >
    <!-- data-test="do-not-send-modal-panel": the reviews-do-not-send e2e spec measures the patient
         picker and its dropdown against this box (rendered-geometry rule, CLAUDE.md). -->
    <div class="dns-body" data-test="do-not-send-modal-panel">
      <p class="dns-intro">
        Patients on this list never get a review request — not automatically after a visit, and not
        from the “Send review request now” button.
      </p>

      <!-- ─── Patient ─────────────────────────────────────────────────── -->
      <div class="dns-field">
        <label id="dns-patient-label" class="dns-label">Patient</label>

        <div
          v-if="selectedPatient"
          class="dns-patient-chip"
          role="group"
          aria-labelledby="dns-patient-label"
          data-test="dns-patient-chip"
        >
          <span class="dns-avatar">{{ initials(selectedPatient.patientName) }}</span>
          <span class="dns-patient-name">{{ selectedPatient.patientName || 'Unknown patient' }}</span>
          <span class="dns-patient-pid">#{{ selectedPatient.patNum }}</span>
          <button
            v-if="canChangePatient"
            type="button"
            class="dns-chip-x"
            data-test="dns-patient-clear"
            :aria-label="'Remove ' + (selectedPatient.patientName || 'patient')"
            @click="clearPatient"
          ><i class="fa fa-times" aria-hidden="true"></i></button>
        </div>

        <!-- HeaderPatientSearch clears its own selection after every pick, so the chosen patient
             lives in `selectedPatient` here, not in the picker. -->
        <div
          v-else-if="canChangePatient"
          class="dns-picker"
          role="group"
          aria-labelledby="dns-patient-label"
          data-test="dns-patient-picker"
        >
          <HeaderPatientSearch
            variant="touch"
            :patients="patients"
            :date-format="dateFormat"
            :refreshing="refreshingPatients"
            @select="onPatientSelect"
            @refresh="refreshPatients"
          />
        </div>
      </div>

      <!-- ─── Duration ────────────────────────────────────────────────── -->
      <div class="dns-field">
        <label class="dns-label">How long</label>
        <div class="dns-durations" role="radiogroup">
          <label
            v-for="d in durations"
            :key="d.value"
            class="dns-duration"
            :class="{ active: form.duration === d.value }"
          >
            <input type="radio" name="dns-duration" :value="d.value" v-model="form.duration" :data-test="'dns-duration-' + d.value" />
            <span>{{ d.label }}</span>
          </label>
        </div>
        <div v-if="form.duration === 'until'" class="dns-until">
          <label class="dns-inline-label" for="dns-until-date">Until</label>
          <input
            id="dns-until-date"
            type="date"
            class="dns-date"
            data-test="dns-until-date"
            :min="tomorrowYmd"
            v-model="form.untilDate"
          />
          <span v-if="endsTodayHint && !form.untilDate" class="dns-hint dns-hint-warn" data-test="dns-ends-today-hint">
            This pause ends today — pick a new date or choose Forever.
          </span>
          <span v-else class="dns-hint">Automatic requests can resume the day after.</span>
        </div>
      </div>

      <!-- ─── Reason ──────────────────────────────────────────────────── -->
      <div class="dns-field">
        <label class="dns-label" for="dns-reason">Reason</label>
        <select id="dns-reason" class="dns-select" data-test="dns-reason" v-model="form.reason">
          <option value="" disabled>Choose a reason</option>
          <option v-for="r in reasons" :key="r.value" :value="r.value">{{ r.label }}</option>
        </select>
      </div>

      <!-- ─── Note ────────────────────────────────────────────────────── -->
      <div class="dns-field">
        <div class="dns-label-row">
          <label class="dns-label" for="dns-note">
            Note <span class="dns-optional">{{ form.reason === 'other' ? '(required for “Other”)' : '(optional)' }}</span>
          </label>
          <span class="dns-counter" :class="{ 'is-over': noteLength > noteMax }" data-test="dns-note-counter">{{ noteLength }}/{{ noteMax }}</span>
        </div>
        <textarea
          id="dns-note"
          class="dns-textarea"
          data-test="dns-note"
          rows="3"
          :maxlength="noteMax"
          placeholder="Only staff see this note."
          v-model="form.note"
        ></textarea>
      </div>

      <div v-if="error" class="dns-error" data-test="dns-error">
        <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
        <span>{{ error }}</span>
      </div>

      <div class="dns-actions">
        <button type="button" class="dns-btn dns-btn-secondary" :disabled="saving" @click="close">Cancel</button>
        <button type="button" class="dns-btn dns-btn-primary" data-test="dns-save" :disabled="saving" @click="save">
          <b-spinner v-if="saving" small class="mr-1" />
          {{ mode === 'edit' ? 'Save changes' : 'Add to list' }}
        </button>
      </div>
    </div>
  </b-modal>
</template>

<script>
/* eslint-disable */
import { mapState, mapActions } from 'vuex';
import moment from 'moment-timezone';
import HeaderPatientSearch from '@/components/Header/HeaderPatientSearch.vue';
import {
  OPTOUT_REASONS, DURATIONS, NOTE_MAX,
  validateDoNotSendForm, buildOptOutPayload, formFromEntry, entryEndsByToday,
  optOutErrorMessage, errorForLog,
} from '../doNotSend';

function blankForm() {
  return { reason: '', note: '', duration: 'forever', untilDate: '' };
}

/**
 * Add a patient to — or edit their entry on — the Reviews "Do not send" list.
 *
 * Used by the Do not send tab (add + edit), the Requests table's row switch and the Patient
 * Profile's ReviewRequestModal (add, patient fixed). No mixin on purpose: the global mixin's
 * created() hooks fetch settings/employees, and this modal only needs four pieces of root state.
 *
 * Props:
 *   show     Boolean — visibility (the parent flips it back to false on `close`)
 *   mode     'add' | 'edit'
 *   patient  { patNum, patientName } — fixed patient; when null in add mode a picker is shown
 *   initial  an existing entry { reason, note, expiresAt, … } (edit mode)
 * Emits:
 *   saved(entry, { patNum, patientName, response }) — after the API accepted it
 *   close({ saved }) — after the modal has hidden
 */
export default {
  name: 'DoNotSendModal',
  components: { HeaderPatientSearch },

  props: {
    show: { type: Boolean, default: false },
    mode: { type: String, default: 'add', validator: (v) => ['add', 'edit'].indexOf(v) > -1 },
    patient: { type: Object, default: null },
    initial: { type: Object, default: null },
  },

  data() {
    return {
      form: blankForm(),
      selectedPatient: null,
      saving: false,
      error: '',
      didSave: false,
      refreshingPatients: false,
      reasons: OPTOUT_REASONS,
      durations: DURATIONS,
      noteMax: NOTE_MAX,
      // Site-local dates, re-read in reset() every time the modal opens. As computeds over
      // moment.tz() they cached forever (they only depend on the timezone) and went stale overnight.
      todayYmd: '',
      tomorrowYmd: '',
      // Editing an entry that ends today: its own date cannot be re-saved, so staff must pick one.
      endsTodayHint: false,
    };
  },

  computed: {
    ...mapState({
      patients: (state) => (state.customer && state.customer.patients) || [],
      siteID: (state) => state.customer && state.customer.siteID,
      user: (state) => state.customer && state.customer.user,
      settingsList: (state) => (state.customer && state.customer.settings) || [],
      sites: (state) => (state.customer && state.customer.sites) || [],
    }),

    timezone() {
      const row = (this.settingsList || []).find((s) => s && s.Property === 'GENERAL_TIMEZONE');
      return (row && row.PropertyValue) || 'America/New_York';
    },

    dateFormat() {
      const site = (this.sites || []).find((s) => s && s.SiteID === this.siteID);
      return (site && site.DateFormat) || 'MM/DD/YYYY';
    },

    // The patient can only be picked when adding without a fixed patient.
    canChangePatient() {
      return this.mode === 'add' && !this.patient;
    },

    noteLength() {
      return (this.form.note || '').length;
    },
  },

  watch: {
    show: {
      immediate: true,
      handler(v) {
        if (v) this.reset();
      },
    },
    // Typing a correction clears the stale message.
    form: {
      deep: true,
      handler() {
        if (this.error) this.error = '';
      },
    },
  },

  methods: {
    ...mapActions('reviews', ['setReviewPatientOptout']),

    reset() {
      this.didSave = false;
      this.saving = false;
      const now = moment.tz(this.timezone);
      this.todayYmd = now.format('YYYY-MM-DD');
      this.tomorrowYmd = now.clone().add(1, 'day').format('YYYY-MM-DD');
      this.selectedPatient = this.patient
        ? { patNum: this.patient.patNum, patientName: this.patient.patientName || '' }
        : null;
      const editing = this.mode === 'edit' && !!this.initial;
      const toYmd = (iso) => this.toSiteYmd(iso);
      this.endsTodayHint = editing && entryEndsByToday(this.initial, toYmd, this.todayYmd);
      this.form = editing ? formFromEntry(this.initial, toYmd, this.todayYmd) : blankForm();
      // Clear after the form watcher has run for the reset itself.
      this.$nextTick(() => { this.error = ''; });
    },

    toSiteYmd(isoUtc) {
      const m = moment.utc(isoUtc).tz(this.timezone);
      return m.isValid() ? m.format('YYYY-MM-DD') : '';
    },

    onPatientSelect(p) {
      if (!p) return;
      this.selectedPatient = {
        patNum: p.PatNum,
        patientName: `${p.FName || ''} ${p.LName || ''}`.trim(),
      };
      this.error = '';
    },

    clearPatient() {
      this.selectedPatient = null;
    },

    async refreshPatients() {
      if (!this.siteID) return;
      this.refreshingPatients = true;
      try {
        await this.$store.dispatch('customer/getPatients', { siteID: this.siteID });
      } catch (err) {
        console.error('[DoNotSendModal] patient refresh failed:', errorForLog(err));
      } finally {
        this.refreshingPatients = false;
      }
    },

    async save() {
      if (this.saving) return;
      const patNum = this.selectedPatient && this.selectedPatient.patNum;
      const err = validateDoNotSendForm(
        { ...this.form, patNum },
        { todayYmd: this.todayYmd, requirePatient: true }
      );
      if (err) {
        this.error = err;
        return;
      }

      this.saving = true;
      this.error = '';
      try {
        const response = await this.setReviewPatientOptout({
          siteID: this.siteID,
          patNum,
          employeeID: this.user && this.user.EmployeeID,
          ...buildOptOutPayload(this.form),
        });
        this.didSave = true;
        this.$emit('saved', (response && response.entry) || null, {
          patNum,
          patientName: this.selectedPatient.patientName,
          response,
        });
        // ⛔ `saving` stays TRUE on success: the modal is animating closed, and re-enabling Save
        // here let a second click POST again. reset() clears it the next time the modal opens.
        this.close();
      } catch (e) {
        console.error('[DoNotSendModal] save failed:', errorForLog(e));
        // 400s (INVALID_REASON, NOTE_REQUIRED, NOTE_TOO_LONG, INVALID_DURATION, INVALID_UNTIL_DATE)
        // map to the form's own sentences; the server's message is developer phrasing, never shown.
        this.error = optOutErrorMessage(e && e.code);
        this.saving = false;
      }
    },

    close() {
      // Every exit (Cancel, Save, X, Esc, backdrop) funnels through b-modal's hide → @hidden, so
      // the parent hears `close` exactly once.
      if (this.$refs.modal && typeof this.$refs.modal.hide === 'function') this.$refs.modal.hide();
      else this.onHidden();
    },

    onHidden() {
      this.$emit('close', { saved: this.didSave });
    },

    initials(name) {
      if (!name) return '?';
      return name.split(/\s+/).filter(Boolean).slice(0, 2).map((n) => n[0].toUpperCase()).join('');
    },
  },
};
</script>

<style lang="scss" scoped>
$primary: #0f766e;
$border: #e2e8f0;
$border-strong: #cbd5e1;
$neutral-500: #64748b;
$neutral-400: #94a3b8;
$neutral-900: #0f172a;

.dns-body { display: flex; flex-direction: column; gap: 16px; }
.dns-intro { font-size: 13px; color: $neutral-500; margin: 0; line-height: 1.5; }

.dns-field { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
.dns-label { font-size: 12px; font-weight: 700; color: #475569; margin: 0; }
.dns-label-row { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; }
.dns-optional { font-weight: 500; color: $neutral-400; }
.dns-hint { font-size: 12px; color: $neutral-400; }
.dns-hint-warn { color: #b45309; font-weight: 600; }

/* Patient picker. Only the multiselect ROOT needs a rule here: Settings/Settings.scss ships an
   UNSCOPED `.multiselect { max-width: 500px }`; HeaderPatientSearch styles the rest itself.
   ⛔ Kept under the scoped .dns-picker ancestor so it cannot reach any other multiselect. */
.dns-picker {
  position: relative;
  width: 100%;

  ::v-deep .multiselect { max-width: none; }
}

.dns-patient-chip {
  display: inline-flex; align-items: center; gap: 9px; align-self: flex-start; max-width: 100%;
  padding: 5px 6px 5px 5px; border: 1px solid #bfe4d8; background: #e9f6f1; border-radius: 999px;
}
.dns-avatar {
  width: 26px; height: 26px; border-radius: 999px; flex-shrink: 0;
  background: #fff; color: #0b5c49; font-size: 11px; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
}
.dns-patient-name { font-weight: 700; font-size: 13.5px; color: $neutral-900; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.dns-patient-pid { font-size: 12px; color: $neutral-500; font-family: monospace; }
.dns-chip-x {
  display: inline-flex; align-items: center; justify-content: center;
  width: 22px; height: 22px; border: none; border-radius: 50%;
  background: transparent; color: #0b5c49; font-size: 11px; cursor: pointer; opacity: 0.75;
  &:hover { background: #cdeadf; opacity: 1; }
}

/* Duration */
.dns-durations { display: flex; flex-wrap: wrap; gap: 8px; }
.dns-duration {
  display: inline-flex; align-items: center; gap: 7px; margin: 0; cursor: pointer;
  border: 1px solid $border; border-radius: 9px; padding: 7px 12px;
  font-size: 13px; font-weight: 600; color: #334155;
  transition: border-color 0.15s ease, background 0.15s ease;
  input { margin: 0; accent-color: $primary; }
  &.active { border-color: #9fd7cd; background: #edfbf6; }
}
.dns-until { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-top: 4px; }
.dns-inline-label { font-size: 13px; font-weight: 600; color: #334155; margin: 0; }
.dns-date {
  border: 1px solid $border-strong; border-radius: 9px; height: 36px; padding: 6px 10px;
  font-family: inherit; font-size: 13.5px; color: #334155; box-sizing: border-box;
}

/* Reason select — explicit height/line-height: Conversations.vue leaks an unscoped
   `select { height: 20px }` app-wide (see ReviewsSettings.vue .rv-select). */
.dns-select {
  appearance: none; -webkit-appearance: none;
  width: 100%; max-width: 320px;
  border: 1px solid $border-strong; border-radius: 9px; background: #fff;
  height: 36px; line-height: 1.4;
  padding: 7px 30px 7px 12px; font-size: 13.5px; color: #334155; font-family: inherit; cursor: pointer;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat; background-position: right 10px center;
}

.dns-textarea {
  width: 100%; box-sizing: border-box; resize: vertical;
  border: 1px solid $border-strong; border-radius: 9px; padding: 8px 11px;
  font-family: inherit; font-size: 13.5px; color: #334155; line-height: 1.45;
}
.dns-counter { font-size: 11.5px; color: $neutral-400; font-variant-numeric: tabular-nums; &.is-over { color: #b91c1c; } }

.dns-error {
  display: flex; align-items: flex-start; gap: 8px;
  background: #fef2f2; border: 1px solid #fecaca; color: #991b1b;
  border-radius: 9px; padding: 9px 12px; font-size: 13px;
  .fa { margin-top: 2px; }
}

.dns-actions { display: flex; justify-content: flex-end; gap: 10px; padding-top: 4px; }
.dns-btn {
  border: none; cursor: pointer; font-family: inherit; font-size: 13px; font-weight: 600;
  padding: 9px 16px; border-radius: 9px; transition: background 0.15s ease;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
}
.dns-btn-secondary { background: #fff; border: 1px solid $border-strong; color: #334155; &:not(:disabled):hover { background: #f8fafc; } }
.dns-btn-primary { background: $primary; color: #fff; &:not(:disabled):hover { background: #0b5f58; } }

@media (max-width: 575px) {
  .dns-select { max-width: none; }
  .dns-actions { flex-direction: column-reverse; .dns-btn { width: 100%; } }
}
</style>
